/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Theme } from '@mui/material';
import { Box, Collapse, Grid, Input, ClickAwayListener } from '@mui/material';
import type { TFunction } from 'i18next';
import type { CSSProperties, FC } from 'react';
import React from 'react';
import {
  CustomIconButton,
  CustomTooltip,
  ToggleMenu,
} from '@opengeoweb/shared';
import {
  ChevronDown,
  ChevronUp,
  Copy,
  Delete,
  DragHandle,
  Visibility,
  VisibilityOff,
  Options,
} from '@opengeoweb/theme';
import { ParameterList } from './ParameterList';
import { RowLayout } from './RowLayout';
import type {
  MoveParameterPayload,
  UpdateTitlePayload,
} from '../../store/types';
import type { Parameter, Plot } from '../TimeSeries/types';
import { TimeSeriesSelectButtonConnect } from '../TimeSeriesSelect/TimeSeriesSelectButtonConnect';

export const PlotRows: FC<{
  panelId: string;
  plot: Plot;
  rowIndex: number;
  showTooltip: boolean;
  deletePlot: (id: string) => void;
  duplicatePlot: (id: string) => void;
  deleteParameter: (id: string) => void;
  togglePlot: (id: string) => void;
  selectPlotId: string;
  setSelectPlotId: (id: string) => void;
  toggleParameter: (id: string) => void;
  patchParameter: (parameter: Partial<Parameter>) => void;
  updateTitle: (payload: UpdateTitlePayload) => void;
  addParameter: (parameter: Parameter) => void;
  moveParameter: (event: MoveParameterPayload) => void;
  t: TFunction;
}> = ({
  panelId,
  plot,
  rowIndex,
  showTooltip,
  deletePlot,
  duplicatePlot,
  togglePlot,
  deleteParameter,
  selectPlotId,
  setSelectPlotId,
  toggleParameter,
  patchParameter,
  updateTitle,
  addParameter,
  moveParameter,
  t,
}) => {
  const [openParameterList, setOpenParameterList] = React.useState(true);
  const [editTitle, setEditTitle] = React.useState(false);
  const [plotTitleLocal, setPlotTitleLocal] = React.useState(plot.title);
  const plotIsEnabled = plot.enabled !== false;
  const styles = getStyles(plotIsEnabled);

  const plotTitleClickAway = (): void => {
    if (editTitle) {
      if (plotTitleLocal !== '' && plotTitleLocal !== plot.title) {
        updateTitle({ plotId: plot.plotId, title: plotTitleLocal });
      }
      setEditTitle(false);
    }
  };

  return (
    <Box key={plot.plotId} sx={styles.plot}>
      <RowLayout
        actions={
          <Grid
            container
            sx={{
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <CustomIconButton
              data-testid="openParameterListButton"
              onClick={(event: React.MouseEvent): void => {
                event.stopPropagation();
                setOpenParameterList(!openParameterList);
              }}
              tooltipTitle={t('timeseries-open-param-list')}
            >
              {openParameterList ? <ChevronUp /> : <ChevronDown />}
            </CustomIconButton>

            <ToggleMenu
              buttonIcon={<Options />}
              buttonSx={{
                margin: '0 2px',
                '&.MuiButtonBase-root': {
                  backgroundColor: 'transparent',
                },
              }}
              buttonTestId={`duplicatePlotMenu_${plot.plotId}`}
              menuItems={[
                {
                  text: t('timeseries-duplicate'),
                  action: (): void => duplicatePlot(plot.plotId),
                  icon: <Copy />,
                },
                {
                  text: t('timeseries-remove'),
                  action: (): void => deletePlot(plot.plotId),
                  icon: <Delete />,
                },
              ]}
              tooltipTitle={t('timeseries-plot-options')}
              variant="flat"
            />
          </Grid>
        }
        collection={
          <TimeSeriesSelectButtonConnect
            isEnabled={plotIsEnabled}
            onClick={(): void => setSelectPlotId(plot.plotId)}
            panelId={panelId}
            plotId={plot.plotId}
            selectPlotId={selectPlotId}
            t={t}
          />
        }
        parameter={
          <ClickAwayListener onClickAway={plotTitleClickAway}>
            <Box onClick={(): void => setEditTitle(true)}>
              {editTitle ? (
                <Input
                  aria-label={t('timeseries-edit-title')}
                  defaultValue={plot.title}
                  inputProps={{
                    'aria-label': `${t('timeseries-edit-title')} ${plot.title}`,
                  }}
                  onChange={(elem): void =>
                    setPlotTitleLocal(elem.target.value)
                  }
                  onFocus={(event): void => event.target.select()}
                  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
                  onKeyDown={(e): boolean | void =>
                    e.key === 'Enter' && plotTitleClickAway()
                  }
                  sx={{
                    ...styles.rowText,
                    backgroundColor: 'geowebColors.textInputField.default.rgba',
                  }}
                />
              ) : (
                <CustomTooltip
                  placement="top"
                  title={t('timeseries-edit-title')}
                >
                  <Box
                    aria-label={`${plot.title}, ${t('timeseries-click-to-edit')}`}
                    sx={{
                      ...styles.rowText,
                      minWidth: '100%',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                    }}
                  >
                    {plot.title}
                  </Box>
                </CustomTooltip>
              )}
            </Box>
          </ClickAwayListener>
        }
        plot={
          <Grid
            container
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <CustomIconButton
              className="handle"
              data-testid={`dragHandleButton_${rowIndex}`}
              disableTouchRipple
              shouldShowAsDisabled={!plotIsEnabled}
              sx={{
                '&.handle:hover': (theme: Theme): CSSProperties => ({
                  ...theme.palette.geowebColors.layerManager.dragHandleHover,
                }),
                cursor: 'grab',
              }}
              tabIndex={-1}
              tooltipTitle={showTooltip ? t('timeseries-drag') : ''}
            >
              <DragHandle />
            </CustomIconButton>
            <CustomIconButton
              data-testid="enableButton"
              onClick={(): void => {
                togglePlot(plot.plotId);
              }}
              shouldShowAsDisabled={!plotIsEnabled}
              tooltipTitle={`${
                plotIsEnabled ? t('timeseries-hide') : t('timeseries-show')
              } ${plot.title}`}
            >
              {plotIsEnabled ? <Visibility /> : <VisibilityOff />}
            </CustomIconButton>
          </Grid>
        }
        sx={styles.row}
      />
      <Collapse
        in={openParameterList}
        sx={styles.parameter}
        timeout="auto"
        unmountOnExit
      >
        <ParameterList
          addParameter={addParameter}
          deleteParameter={deleteParameter}
          moveParameter={moveParameter}
          panelId={panelId}
          patchParameter={patchParameter}
          plot={plot}
          plotIsEnabled={plotIsEnabled}
          t={t}
          toggleParameter={toggleParameter}
        />
      </Collapse>
    </Box>
  );
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getStyles = (isEnabled: boolean) => ({
  plot: {
    boxShadow: 3,
    borderRadius: 0.75,
    backgroundColor: 'geowebColors.background.surface',
    '&.sortable-ghost': {
      opacity: 0.5,
    },
  },
  row: {
    backgroundColor: isEnabled
      ? 'geowebColors.layerManager.tableRowDefaultCardContainer.fill'
      : 'geowebColors.layerManager.tableRowDisabledCardContainer.fill',
    borderStyle: 'solid',
    borderWidth: '1px',
    borderColor: 'geowebColors.cards.cardContainerBorder',
    borderRadius: 1,
    marginBottom: 0.25,
    height: 34,
    alignItems: 'center',
  },
  rowText: {
    fontSize: 14,
    fontWeight: 500,
    color: isEnabled
      ? 'geowebColors.layerManager.tableRowDefaultText.color'
      : 'geowebColors.layerManager.tableRowDisabledText.color',
  },
  parameter: {
    marginBottom: 0.25,
  },
});
